import type { WineColour } from "../scales.js";
import type { StepNote } from "./types.js";

export const ARCHETYPE_STYLES = ["white", "rose", "red", "sparkling"] as const;
export type ArchetypeStyle = (typeof ARCHETYPE_STYLES)[number];

/**
 * What a comparative step's wine archetype looks like, in facts a tasting can
 * confirm. Every key present must match; absent keys leave the wine free.
 */
export interface ArchetypePattern {
  readonly grape?: string;
  readonly producer?: string;
  readonly style?: ArchetypeStyle;
  /** ISO 3166-1 alpha-2. */
  readonly country?: string;
  readonly region?: string;
  readonly vintage?: number;
}

export interface Archetype {
  /** 0-based slot in the step, unique within it. */
  readonly index: number;
  readonly nl: string;
  readonly en: string;
  readonly pattern: ArchetypePattern;
}

/** What is known about the wine behind a note: the facts the matcher may compare. */
export interface WineFacts {
  readonly grapes: readonly string[];
  readonly producer?: string;
  readonly colour?: WineColour;
  readonly sparkling?: boolean;
  readonly country?: string;
  readonly region?: string;
  readonly appellation?: string;
  readonly vintage?: number | null;
}

export type PatternAxis = keyof ArchetypePattern;

export interface ArchetypeFit {
  readonly archetype: number;
  readonly fits: boolean;
  /** The axes that do not match, in a fixed order, so the step can say why in our words. */
  readonly mismatches: readonly PatternAxis[];
}

/** Case, diacritics and punctuation do not make two spellings of a name different. */
function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function styleMatches(style: ArchetypeStyle, wine: WineFacts): boolean {
  return style === "sparkling" ? wine.sparkling === true : wine.colour === style;
}

/**
 * Does `wine` fit `archetype`? A fact the wine does not state cannot be
 * confirmed and counts as a mismatch. The answer advises and never forbids: the
 * learner's judgment outranks ours, so a step may still take a note that does not fit.
 */
export function fitArchetype(wine: WineFacts, archetype: Archetype): ArchetypeFit {
  const p = archetype.pattern;
  const mismatches: PatternAxis[] = [];
  if (p.grape !== undefined && !wine.grapes.some((g) => fold(g) === fold(p.grape ?? ""))) {
    mismatches.push("grape");
  }
  if (p.producer !== undefined && fold(wine.producer ?? "") !== fold(p.producer)) {
    mismatches.push("producer");
  }
  if (p.style !== undefined && !styleMatches(p.style, wine)) mismatches.push("style");
  if (p.country !== undefined && (wine.country ?? "").toUpperCase() !== p.country.toUpperCase()) {
    mismatches.push("country");
  }
  if (
    p.region !== undefined &&
    ![wine.region, wine.appellation].some(
      (r) => r !== undefined && fold(r) === fold(p.region ?? ""),
    )
  ) {
    mismatches.push("region");
  }
  if (p.vintage !== undefined && wine.vintage !== p.vintage) mismatches.push("vintage");
  return { archetype: archetype.index, fits: mismatches.length === 0, mismatches };
}

/** The indexes of every archetype the wine fits, ascending. */
export function fittingArchetypes(wine: WineFacts, archetypes: readonly Archetype[]): number[] {
  return archetypes
    .filter((archetype) => fitArchetype(wine, archetype).fits)
    .map((archetype) => archetype.index)
    .toSorted((a, b) => a - b);
}

/** How many parts a pattern names: the more it names, the more specific the style. */
const specificity = (archetype: Archetype): number => Object.keys(archetype.pattern).length;

/**
 * The archetype a bottle fits best, or undefined when it fits none. Of the styles it fits, the one
 * that names more parts of the bottle wins (a dry Mosel Riesling Kabinett over a Mosel Riesling),
 * and a tie goes to the lower index, so the answer is the same every time. It reads identity only:
 * price and seller are not on a bottle's facts, so they cannot change the answer.
 */
export function bestArchetype(
  wine: WineFacts,
  archetypes: readonly Archetype[],
): Archetype | undefined {
  const fitting = archetypes.filter((archetype) => fitArchetype(wine, archetype).fits);
  return fitting.toSorted((a, b) => specificity(b) - specificity(a) || a.index - b.index)[0];
}

/**
 * Attaches a note to its archetype: the later note replaces the earlier one for
 * that archetype, the same note twice stays one record, and the step holds one
 * note per archetype, ordered by archetype. The input is not touched.
 */
export function attachNote(notes: readonly StepNote[], note: StepNote): StepNote[] {
  return [...notes.filter((held) => held.archetype !== note.archetype), note].toSorted(
    (a, b) => a.archetype - b.archetype,
  );
}

/** `true` when every archetype slot `0…count-1` holds exactly one note. */
export function stepNotesComplete(notes: readonly StepNote[], archetypeCount: number): boolean {
  if (archetypeCount < 1) return false;
  const filled = new Set(notes.map((note) => note.archetype));
  return (
    filled.size === notes.length &&
    Array.from({ length: archetypeCount }, (_, i) => i).every((i) => filled.has(i))
  );
}
